import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import InputWithLabel from "../../components/form/input/InputWithLabel";
import axiosInstance from "../../hooks/axios";
import { formatUkDate } from "../../utils/dateUtils";

type Tab = "top-routes" | "price-checker";

type TopRouteRow = {
  id: string;
  lastChangedAt: string;
  fromArea: string;
  toArea: string;
  popularPrice: number;
  yourPrice: number | null;
  papId: string | null;
  hasPap: boolean;
};

type VehicleTypeOption = {
  id: string;
  description: string;
  seatCapacity: number | null;
  isMileageBase: boolean;
};

type PriceCheckResult = {
  fromArea: string;
  toArea: string;
  marketPrice: number | null;
  yourPapPrice: number | null;
  papId: string | null;
  hasTopRoute: boolean;
  fareCalculation?: {
    totalFare?: number;
    calculationType?: string;
    breakdown?: Record<string, unknown>;
  };
};

function formatMoney(n: number): string {
  return `£${n.toFixed(2)}`;
}

function formatLastChanged(iso: string): string {
  return formatUkDate(iso, {
    weekday: "short",
    day: "2-digit",
    month: "short",
  });
}

export default function PriceChecker() {
  const [tab, setTab] = useState<Tab>("top-routes");
  const [fleetTab, setFleetTab] = useState<"standard" | "executive">("standard");
  const [routes, setRoutes] = useState<TopRouteRow[]>([]);
  const [sectionTitle, setSectionTitle] = useState("4-SEATERS");
  const [vehicleTypeId, setVehicleTypeId] = useState("");
  const [vehicleTypes, setVehicleTypes] = useState<VehicleTypeOption[]>([]);
  const [loadingRoutes, setLoadingRoutes] = useState(true);
  const [message, setMessage] = useState("");

  const [updateRoute, setUpdateRoute] = useState<TopRouteRow | null>(null);
  const [updatePrice, setUpdatePrice] = useState("");
  const [savingPap, setSavingPap] = useState(false);

  const [checkForm, setCheckForm] = useState({
    fromPostCode: "",
    toPostCode: "",
    distance: 15,
    pickupDateTime: new Date().toISOString().slice(0, 16),
    vehicleTypeId: "",
  });
  const [checkResult, setCheckResult] = useState<PriceCheckResult | null>(null);
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState("");

  const loadTopRoutes = useCallback(async () => {
    setLoadingRoutes(true);
    try {
      const res = await axiosInstance.get<{
        routes: TopRouteRow[];
        sectionTitle: string;
        vehicleTypeId: string | null;
      }>("/top-routes", {
        params: {
          fleetCategory: fleetTab,
          fuelCategory: "petrol_diesel_hybrid",
          seatCategory: "4",
        },
      });
      setRoutes(res.data.routes);
      setSectionTitle(res.data.sectionTitle);
      setVehicleTypeId(res.data.vehicleTypeId ?? "");
      const typesRes = await axiosInstance.get<VehicleTypeOption[]>(
        "/postcode-area-prices/vehicle-types",
        { params: { fleetCategory: fleetTab } },
      );
      setVehicleTypes(typesRes.data);
    } catch {
      setMessage("Failed to load top routes");
    } finally {
      setLoadingRoutes(false);
    }
  }, [fleetTab]);

  useEffect(() => {
    if (tab === "top-routes") loadTopRoutes();
  }, [tab, loadTopRoutes]);

  const openUpdate = (row: TopRouteRow) => {
    setUpdateRoute(row);
    setUpdatePrice(
      row.yourPrice != null ? String(row.yourPrice) : String(row.popularPrice),
    );
  };

  const closeUpdate = () => {
    setUpdateRoute(null);
    setUpdatePrice("");
  };

  const handleSavePap = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!updateRoute) return;
    const price = Number(updatePrice);
    if (Number.isNaN(price) || price <= 0) {
      setMessage("Enter a valid price");
      return;
    }
    const baseId = vehicleTypeId || vehicleTypes[0]?.id;
    if (!baseId) {
      setMessage("No vehicle type available");
      return;
    }

    setSavingPap(true);
    setMessage("");
    try {
      const prices: Record<string, number> = { [baseId]: price };
      const payload = {
        fuelCategory: "petrol_diesel_hybrid",
        fleetCategory: fleetTab,
        pickupPostcodes: updateRoute.fromArea,
        dropoffPostcodes: updateRoute.toArea,
        prices,
        baseVehicleTypeId: baseId,
        autoUpliftOtherVehicles: true,
        createReverse: true,
        status: true,
      };

      if (updateRoute.papId) {
        await axiosInstance.put(
          `/postcode-area-prices/${updateRoute.papId}`,
          payload,
        );
        setMessage("Postcode area price updated");
      } else {
        await axiosInstance.post("/postcode-area-prices", payload);
        setMessage("Postcode area price created — other vehicle sizes use your uplifts");
      }
      closeUpdate();
      await loadTopRoutes();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Failed to save price";
      setMessage(String(msg));
    } finally {
      setSavingPap(false);
    }
  };

  const handleCheckPrice = async (e: React.FormEvent) => {
    e.preventDefault();
    setChecking(true);
    setCheckError("");
    setCheckResult(null);
    try {
      const res = await axiosInstance.post<PriceCheckResult>(
        "/top-routes/check-price",
        {
          fromPostCode: checkForm.fromPostCode,
          toPostCode: checkForm.toPostCode,
          fleetCategory: fleetTab,
          fuelCategory: "petrol_diesel_hybrid",
          vehicleTypeId: checkForm.vehicleTypeId || vehicleTypeId || undefined,
          distance: checkForm.distance,
          pickupDateTime: new Date(checkForm.pickupDateTime).toISOString(),
        },
      );
      setCheckResult(res.data);
    } catch {
      setCheckError("Price check failed");
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    if (tab !== "price-checker" || vehicleTypes.length) return;
    axiosInstance
      .get<VehicleTypeOption[]>("/postcode-area-prices/vehicle-types", {
        params: { fleetCategory: fleetTab },
      })
      .then((res) => {
        setVehicleTypes(res.data);
        const base =
          res.data.find((v) => v.isMileageBase) ??
          res.data.find((v) => (v.seatCapacity ?? 99) <= 4);
        setCheckForm((f) => ({
          ...f,
          vehicleTypeId: f.vehicleTypeId || base?.id || "",
        }));
      })
      .catch(() => {});
  }, [tab, fleetTab, vehicleTypes.length]);

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <PageBreadcrumb pageTitle="Top Routes & Price checker" />
        <a
          href="https://www.gov.uk/find-postcode"
          target="_blank"
          rel="noreferrer"
          className="text-sm text-brand-600 hover:text-brand-700 hover:underline dark:text-brand-400"
        >
          Help
        </a>
      </div>

      <div className="mt-4 flex gap-2 border-b">
        <button
          type="button"
          onClick={() => setTab("top-routes")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
            tab === "top-routes"
              ? "border-brand-500 text-brand-600 dark:text-brand-400"
              : "border-transparent text-gray-500"
          }`}
        >
          Top Routes
        </button>
        <button
          type="button"
          onClick={() => setTab("price-checker")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
            tab === "price-checker"
              ? "border-brand-500 text-brand-600 dark:text-brand-400"
              : "border-transparent text-gray-500"
          }`}
        >
          Price checker
        </button>
      </div>

      <div className="mt-2 flex gap-2 border-b">
        {(["standard", "executive"] as const).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFleetTab(f)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px capitalize ${
              fleetTab === f
                ? "border-brand-500 text-brand-600 dark:text-brand-400"
                : "border-transparent text-gray-500"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {message && (
        <p className="mt-4 text-sm text-green-700 bg-green-50 px-3 py-2 rounded">
          {message}
        </p>
      )}

      {tab === "top-routes" && (
        <>
          <p className="mt-4 text-sm text-gray-600">
            This page shows the most popular and least competitive routes in your
            region. Compare the best performing price point with your current
            price. Airport-related benchmark prices exclude pickup and dropoff
            charges.
          </p>

          <h2 className="mt-6 text-sm font-bold tracking-wide text-gray-800 uppercase">
            {sectionTitle}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Updating a price here creates a{" "}
            <Link to="/postcode-area-prices" className="text-brand-600 hover:text-brand-700 hover:underline dark:text-brand-400">
              Postcode Area Price (PAP)
            </Link>{" "}
            for that route. Other vehicle sizes and fleet types are filled from
            your existing uplifts.
          </p>

          {loadingRoutes ? (
            <p className="mt-6 text-gray-500">Loading routes…</p>
          ) : routes.length === 0 ? (
            <p className="mt-6 text-gray-500">
              No benchmark routes for this fleet yet.{" "}
              {fleetTab === "executive"
                ? "Try Standard fleet or add market data."
                : "Contact support to import regional data."}
            </p>
          ) : (
            <div className="mt-4 overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03]">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-600">
                    <th className="px-4 py-3 font-medium">Last changed</th>
                    <th className="px-4 py-3 font-medium">From</th>
                    <th className="px-4 py-3 font-medium">To</th>
                    <th className="px-4 py-3 font-medium">
                      Most popular price — 4 seater
                    </th>
                    <th className="px-4 py-3 font-medium">
                      Your price — 4 seater
                    </th>
                    <th className="px-4 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {routes.map((row) => (
                    <tr key={row.id} className="border-b last:border-0">
                      <td className="px-4 py-3 whitespace-nowrap">
                        {formatLastChanged(row.lastChangedAt)}
                      </td>
                      <td className="px-4 py-3 font-medium">{row.fromArea}</td>
                      <td className="px-4 py-3 font-medium">{row.toArea}</td>
                      <td className="px-4 py-3">{formatMoney(row.popularPrice)}</td>
                      <td className="px-4 py-3">
                        {row.hasPap && row.yourPrice != null ? (
                          formatMoney(row.yourPrice)
                        ) : (
                          <span className="text-gray-500 italic">
                            You don&apos;t have a PAP created for this route
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => openUpdate(row)}
                          className="text-brand-600 hover:text-brand-700 hover:underline dark:text-brand-400"
                        >
                          Update my price
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === "price-checker" && (
        <>
          <p className="mt-4 text-sm text-gray-600">
            Enter pickup and dropoff outward postcodes (e.g. TW6 and W2) to compare
            market benchmarks, your PAP, and a live fare estimate.
          </p>

          <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] lg:p-6 max-w-xl">
            {checkError && (
              <div className="mb-4 p-3 bg-red-50 text-red-600 rounded text-sm">
                {checkError}
              </div>
            )}
            <form onSubmit={handleCheckPrice} className="space-y-4">
              <InputWithLabel
                label="From postcode area"
                name="fromPostCode"
                value={checkForm.fromPostCode}
                onChange={(e) =>
                  setCheckForm({ ...checkForm, fromPostCode: e.target.value.toUpperCase() })
                }
                placeholder="TW6"
                required
              />
              <InputWithLabel
                label="To postcode area"
                name="toPostCode"
                value={checkForm.toPostCode}
                onChange={(e) =>
                  setCheckForm({ ...checkForm, toPostCode: e.target.value.toUpperCase() })
                }
                placeholder="W2"
                required
              />
              <InputWithLabel
                label="Distance (miles)"
                name="distance"
                type="number"
                value={String(checkForm.distance)}
                onChange={(e) =>
                  setCheckForm({ ...checkForm, distance: Number(e.target.value) })
                }
              />
              <InputWithLabel
                label="Pickup date & time"
                name="pickupDateTime"
                type="datetime-local"
                value={checkForm.pickupDateTime}
                onChange={(e) =>
                  setCheckForm({ ...checkForm, pickupDateTime: e.target.value })
                }
              />
              {vehicleTypes.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vehicle type
                  </label>
                  <select
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    value={checkForm.vehicleTypeId || vehicleTypeId}
                    onChange={(e) =>
                      setCheckForm({ ...checkForm, vehicleTypeId: e.target.value })
                    }
                  >
                    {vehicleTypes.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.description}
                        {v.seatCapacity != null ? ` (${v.seatCapacity} seats)` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <button
                type="submit"
                disabled={checking}
                className="px-4 py-2 bg-brand-500 text-white rounded-lg text-sm disabled:opacity-60"
              >
                {checking ? "Checking…" : "Check price"}
              </button>
            </form>
          </div>

          {checkResult && (
            <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] lg:p-6 space-y-3 text-sm">
              <h3 className="font-semibold text-gray-800">
                {checkResult.fromArea} → {checkResult.toArea}
              </h3>
              <p>
                <span className="text-gray-600">Market (top route): </span>
                {checkResult.marketPrice != null
                  ? formatMoney(checkResult.marketPrice)
                  : "No benchmark on file"}
              </p>
              <p>
                <span className="text-gray-600">Your PAP (4 seater): </span>
                {checkResult.yourPapPrice != null
                  ? formatMoney(checkResult.yourPapPrice)
                  : "You don't have a PAP created for this route"}
              </p>
              {checkResult.fareCalculation?.totalFare != null && (
                <p>
                  <span className="text-gray-600">Calculated fare: </span>
                  {formatMoney(Number(checkResult.fareCalculation.totalFare))}
                  {checkResult.fareCalculation.calculationType && (
                    <span className="text-gray-500 ml-2">
                      ({checkResult.fareCalculation.calculationType})
                    </span>
                  )}
                </p>
              )}
              {checkResult.papId && (
                <Link
                  to="/postcode-area-prices"
                  className="inline-block text-brand-600 hover:text-brand-700 hover:underline dark:text-brand-400"
                >
                  Edit in Postcode Area Prices
                </Link>
              )}
            </div>
          )}
        </>
      )}

      {updateRoute && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-800">
              Update my price
            </h3>
            <p className="mt-2 text-sm text-gray-600">
              Route: {updateRoute.fromArea} → {updateRoute.toArea}. Market price:{" "}
              {formatMoney(updateRoute.popularPrice)}. This creates or updates a
              PAP and applies uplifts to other vehicle sizes.
            </p>
            <form onSubmit={handleSavePap} className="mt-4 space-y-4">
              <InputWithLabel
                label="Your price — 4 seater (£)"
                name="price"
                type="number"
                value={updatePrice}
                onChange={(e) => setUpdatePrice(e.target.value)}
                required
              />
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={closeUpdate}
                  className="px-4 py-2 text-sm text-gray-700 border rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingPap}
                  className="px-4 py-2 text-sm bg-brand-500 text-white rounded-lg disabled:opacity-60"
                >
                  {savingPap ? "Saving…" : updateRoute.papId ? "Update PAP" : "Create PAP"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
